import { gql } from '@apollo/client';
import { apolloClient } from '../graphql/client';
import { SourceInput } from '../types';

class SourceService {
  // Get all sources
  async getSources() {
    const query = gql`
      query GetSources {
        sources {
          id
          sourceId
          name
          description
          createdDate
          isActive
        }
      }
    `;

    try {
      console.log('Attempting to query GraphQL for sources at:', process.env.REACT_APP_GRAPHQL_ENDPOINT);
      const result = await apolloClient.query({
        query,
        fetchPolicy: 'network-only'
      });
      console.log('GraphQL sources response:', result);
      return result.data.sources;
    } catch (error: any) {
      console.error('Error fetching sources:', error);
      console.error('Network error:', error.networkError);
      console.error('GraphQL errors:', error.graphQLErrors);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to fetch sources: ${error.message}`);
      }
    }
  }

  // Get source by ID
  async getSourceById(id: string) {
    const query = gql`
      query GetSource($id: String!) {
        source(id: $id) {
          id
          code
          name
          description
          status
          tenantId
          createdDate
          lastModifiedDate
        }
      }
    `;

    try {
      const result = await apolloClient.query({
        query,
        variables: { id },
        fetchPolicy: 'network-only'
      });
      return result.data.source;
    } catch (error: any) {
      console.error('Error fetching source by ID:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to fetch source: ${error.message}`);
      }
    }
  }

  // Create source
  async createSource(input: SourceInput) {
    const mutation = gql`
      mutation CreateSource($input: SourceInput!) {
        createSource(input: $input) {
          id
          sourceId
          name
          description
          createdDate
          isActive
        }
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { input }
      });
      return result.data.createSource;
    } catch (error: any) {
      console.error('Error creating source:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to create source: ${error.message}`);
      }
    }
  }

  // Update source
  async updateSource(id: string, input: SourceInput) {
    const mutation = gql`
      mutation UpdateSource($id: String!, $input: SourceInput!) {
        updateSource(id: $id, input: $input) {
          id
          sourceId
          name
          description
          createdDate
          isActive
        }
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { id, input }
      });
      return result.data.updateSource;
    } catch (error: any) {
      console.error('Error updating source:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to update source: ${error.message}`);
      }
    }
  }

  // Delete source
  async deleteSource(id: string) {
    const mutation = gql`
      mutation DeleteSource($id: String!) {
        deleteSource(id: $id)
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { id }
      });
      return result.data.deleteSource;
    } catch (error: any) {
      console.error('Error deleting source:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to delete source: ${error.message}`);
      }
    }
  }
}

const sourceService = new SourceService();
export default sourceService;
